import { useAuth } from '@clerk/clerk-react';
import React from 'react';
import { useCurrentOrgContext } from '../hooks/use-current-org';

/**
 * The BFF refused the asserted organization: the caller is not a member of it, or it does not exist.
 * The stored org id is stale and must be forgotten.
 */
export const ORG_FORBIDDEN_TYPE = 'https://redbax.io/errors/org-forbidden';

/**
 * The organization is suspended (ADR-007). The caller IS a legitimate member and the selection must
 * SURVIVE — the suspended-org modal is rendered from exactly that state. Clearing it here would drop
 * the user into onboarding as though they had no org, which is both wrong and unrecoverable from the
 * UI, since they cannot re-select an org they can no longer see.
 */
export const ORG_INACTIVE_TYPE = 'https://redbax.io/errors/org-inactive';

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: string,
  ) {
    super(`API Error ${status}: ${body}`);
    this.name = 'ApiError';
  }
}

/**
 * Reads the ProblemDetails `type` out of a response body, or null when the body is not
 * ProblemDetails. Both refusals are 403, so the status code alone cannot tell them apart — the type
 * is the only thing that distinguishes "you were removed" from "your org is suspended".
 */
function problemType(body: string): string | null {
  try {
    const parsed = JSON.parse(body) as { type?: unknown };
    return typeof parsed?.type === 'string' ? parsed.type : null;
  } catch {
    return null;
  }
}

export class BffClient {
  private getToken: () => Promise<string | null>;
  private getOrgId: () => string | null;
  private baseUrl: string;
  private onOrgRefused?: () => void;

  constructor(
    getToken: () => Promise<string | null>,
    getOrgId: () => string | null,
    baseUrl = '',
    onOrgRefused?: () => void,
  ) {
    this.getToken = getToken;
    this.getOrgId = getOrgId;
    this.baseUrl = baseUrl.replace(/\/$/, '');
    this.onOrgRefused = onOrgRefused;
  }

  private buildHeaders(token: string, extra?: Record<string, string>): Record<string, string> {
    const headers: Record<string, string> = { Authorization: `Bearer ${token}`, ...extra };
    const orgId = this.getOrgId();
    if (orgId) headers['X-Redbax-Org-Id'] = orgId;
    return headers;
  }

  /**
   * Single exit for every non-2xx response. Defence in depth: the BFF already refuses the org, but a
   * client that keeps asserting a refused org stays locked out of every guarded route until a reload.
   * Forgetting the selection makes the user recover on their own — the org switcher and bootstrap are
   * allowlisted on the BFF precisely so they still work while no org is selected.
   */
  private async fail(res: Response): Promise<never> {
    const body = await res.text();

    if (res.status === 403 && problemType(body) === ORG_FORBIDDEN_TYPE) {
      this.onOrgRefused?.();
    }

    throw new ApiError(res.status, body);
  }

  async get<T>(path: string): Promise<T> {
    const token = await this.getToken();
    if (!token) throw new ApiError(401, 'No authentication token available');
    const res = await fetch(`${this.baseUrl}${path}`, {
      headers: this.buildHeaders(token),
    });
    if (!res.ok) return this.fail(res);
    const text = await res.text();
    if (!text) return null as T;
    return JSON.parse(text);
  }

  async post<T>(path: string, body?: unknown): Promise<T> {
    const token = await this.getToken();
    if (!token) throw new ApiError(401, 'No authentication token available');
    const res = await fetch(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers: this.buildHeaders(token, { 'Content-Type': 'application/json' }),
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) return this.fail(res);
    if (res.status === 204) return undefined as T;
    return res.json();
  }

  async put<T>(path: string, body: unknown): Promise<T> {
    const token = await this.getToken();
    if (!token) throw new ApiError(401, 'No authentication token available');
    const res = await fetch(`${this.baseUrl}${path}`, {
      method: 'PUT',
      headers: this.buildHeaders(token, { 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
    });
    if (!res.ok) return this.fail(res);
    if (res.status === 204) return undefined as T;
    return res.json();
  }

  async postVoid(path: string, body?: unknown): Promise<void> {
    const token = await this.getToken();
    if (!token) throw new ApiError(401, 'No authentication token available');
    const res = await fetch(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers: this.buildHeaders(token, { 'Content-Type': 'application/json' }),
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) return this.fail(res);
  }

  async delete(path: string): Promise<void> {
    const token = await this.getToken();
    if (!token) throw new ApiError(401, 'No authentication token available');
    const res = await fetch(`${this.baseUrl}${path}`, {
      method: 'DELETE',
      headers: this.buildHeaders(token),
    });
    if (!res.ok) return this.fail(res);
  }
}

const BffClientContext = React.createContext<BffClient | null>(null);

export function BffClientProvider({ children }: { children: React.ReactNode }) {
  const { getToken } = useAuth();
  const { currentOrg, clearCurrentOrg } = useCurrentOrgContext();

  const orgIdRef = React.useRef<string | null>(null);
  orgIdRef.current = currentOrg?.orgId ?? null;

  // Held in a ref for the same reason as orgIdRef: the client is memoised, so reading the callback
  // through a ref keeps the latest one reachable without rebuilding the client on every render.
  const clearCurrentOrgRef = React.useRef(clearCurrentOrg);
  clearCurrentOrgRef.current = clearCurrentOrg;

  const baseUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_BFF_BASE_URL as string) || '';

  const client = React.useMemo(
    () => new BffClient(getToken, () => orgIdRef.current, baseUrl, () => clearCurrentOrgRef.current()),
    [getToken, baseUrl],
  );

  return React.createElement(BffClientContext.Provider, { value: client }, children);
}

export function useBffClient(): BffClient {
  const client = React.useContext(BffClientContext);
  if (!client) {
    throw new Error('useBffClient must be used within a BffClientProvider');
  }
  return client;
}
